/**
 * كتالوج إعداد المؤسسة.
 *
 * ما تُعرّفه الشركةُ عن نفسها مرّةً ثمّ يُختار منه في كلّ ملفٍّ بعده:
 * وحداتُها التنظيميّة، ومراكزُ تكلفتها، وورديّاتُها، وأنماطُ العمل فيها.
 *
 * ## ولماذا يُختار ولا يُكتب
 *
 * «قسم التصميم» مكتوبًا بخطّ اليد في مئة ملفٍّ يصير «التصميم» و«الديزاين»
 * و«قسم التصميم » بمسافةٍ زائدة — فلا يُجمع تقريرٌ ولا تُحسب تكلفةُ وحدة.
 * والاختيارُ من قائمةٍ يجعل الاسمَ واحدًا مهما تعدّد من يكتبه.
 */
import type { Labelled } from './common.js';

/* ==========================================================================
   الوحدات التنظيمية
   ========================================================================== */

export const ORG_UNIT_KINDS = ['DIVISION', 'DEPARTMENT', 'TEAM'] as const;
export type OrgUnitKind = (typeof ORG_UNIT_KINDS)[number];

export interface OrgUnitKindDef extends Labelled<OrgUnitKind> {
  description: string;
  /** عمقُها المعتاد في الشجرة — يُقترح عند الإنشاء ولا يُفرض. */
  depth: number;
}

export const ORG_UNIT_KIND_DEFS: readonly OrgUnitKindDef[] = [
  {
    key: 'DIVISION',
    label: 'إدارة',
    kind: 'brand',
    description: 'أعلى تقسيم — تحتها أقسام.',
    depth: 1,
  },
  {
    key: 'DEPARTMENT',
    label: 'قسم',
    kind: 'info',
    description: 'داخل إدارة — تحته فرق.',
    depth: 2,
  },
  {
    key: 'TEAM',
    label: 'فريق',
    kind: 'neutral',
    description: 'أصغرُ وحدةٍ يعمل فيها الناس معًا.',
    depth: 3,
  },
] as const;

export const ORG_UNIT_KIND_MAP = Object.fromEntries(
  ORG_UNIT_KIND_DEFS.map((d) => [d.key, d]),
) as Readonly<Record<OrgUnitKind, OrgUnitKindDef>>;

/* ==========================================================================
   نمط العمل
   ========================================================================== */

/**
 * من أين يعمل — لا بأيّ عقدٍ يعمل.
 *
 * فرقٌ يُخلط كثيرًا: «دوام كامل» نوعُ تعاقد، و«عن بُعد» مكانُ عمل. ومن
 * يعمل بدوامٍ كاملٍ من بيته يحتاج الاثنين ليوصَف — وحقلٌ واحدٌ يجمعهما
 * يجبره على اختيار أحدهما.
 */
export const WORK_MODES = ['ONSITE', 'REMOTE', 'HYBRID', 'FIELD'] as const;
export type WorkMode = (typeof WORK_MODES)[number];

export interface WorkModeDef extends Labelled<WorkMode> {
  description: string;
  /** هل يُنتظر منه تسجيلُ حضورٍ من موقع الشركة. */
  onSite: boolean;
}

export const WORK_MODE_DEFS: readonly WorkModeDef[] = [
  {
    key: 'ONSITE',
    label: 'من المقرّ',
    kind: 'success',
    description: 'يحضر إلى موقع العمل كلَّ يوم.',
    onSite: true,
  },
  {
    key: 'REMOTE',
    label: 'عن بُعد',
    kind: 'info',
    description: 'يعمل من مكانه — ولا يُنتظر منه تسجيلُ حضورٍ من الموقع.',
    onSite: false,
  },
  {
    key: 'HYBRID',
    label: 'مختلط',
    kind: 'brand',
    description: 'أيّامٌ في المقرّ وأيّامٌ من بعيد.',
    onSite: true,
  },
  {
    key: 'FIELD',
    label: 'ميداني',
    kind: 'warning',
    description: 'عملُه عند العملاء لا في المقرّ.',
    onSite: false,
  },
] as const;

export const WORK_MODE_MAP = Object.fromEntries(
  WORK_MODE_DEFS.map((d) => [d.key, d]),
) as Readonly<Record<WorkMode, WorkModeDef>>;

/* ==========================================================================
   الورديات
   ========================================================================== */

export interface ScheduleShape {
  workDays: readonly number[];
  startTime: string;
  endTime: string;
  breakMinutes: number;
  overnight: boolean;
}

/** دقائقُ اليوم من نصّ «ساعة:دقيقة». */
export function minutesOfTime(hhmm: string): number {
  const [h, m] = hhmm.split(':').map((x) => Number(x) || 0);
  return (h ?? 0) * 60 + (m ?? 0);
}

/**
 * ساعاتُ الوردية الصافية.
 *
 * والعابرةُ لمنتصف الليل تُحسب بإضافة يوم: من عمل من العاشرة مساءً إلى
 * السادسة صباحًا عمل ثماني ساعات لا سالبَ ستَّ عشرة.
 */
export function scheduleHours(s: ScheduleShape): number {
  const start = minutesOfTime(s.startTime);
  let end = minutesOfTime(s.endTime);
  if (s.overnight || end <= start) end += 24 * 60;
  const net = end - start - s.breakMinutes;
  return Math.round((net / 60) * 100) / 100;
}

/** ساعاتُ الأسبوع — الساعاتُ في عدد أيّام العمل. */
export function weeklyHours(s: ScheduleShape): number {
  return Math.round(scheduleHours(s) * s.workDays.length * 100) / 100;
}

/**
 * الوردياتُ المقترحةُ عند أوّل فتح.
 *
 * لا يقف من يُعدّ المؤسسةَ أمام قائمةٍ فارغة. وكلُّها تُعدَّل وتُحذف —
 * البذرةُ اقتراحٌ لا حكم.
 */
export const DEFAULT_SCHEDULES = [
  {
    name: 'الدوام الصباحي',
    workDays: [0, 1, 2, 3, 4],
    startTime: '09:00',
    endTime: '17:00',
    breakMinutes: 60,
    graceMinutes: 15,
    overnight: false,
    isDefault: true,
  },
  {
    name: 'الوردية المسائية',
    workDays: [0, 1, 2, 3, 4],
    startTime: '14:00',
    endTime: '22:00',
    breakMinutes: 45,
    graceMinutes: 15,
    overnight: false,
    isDefault: false,
  },
  {
    name: 'الوردية الليلية',
    workDays: [0, 1, 2, 3, 4],
    startTime: '22:00',
    endTime: '06:00',
    breakMinutes: 45,
    graceMinutes: 15,
    overnight: true,
    isDefault: false,
  },
] as const;

/* ==========================================================================
   أيام الأسبوع
   ========================================================================== */

export const WEEKDAY_DEFS: readonly { key: number; label: string; short: string }[] =
  [
    { key: 0, label: 'الأحد', short: 'ح' },
    { key: 1, label: 'الاثنين', short: 'ن' },
    { key: 2, label: 'الثلاثاء', short: 'ث' },
    { key: 3, label: 'الأربعاء', short: 'ر' },
    { key: 4, label: 'الخميس', short: 'خ' },
    { key: 5, label: 'الجمعة', short: 'ج' },
    { key: 6, label: 'السبت', short: 'س' },
  ] as const;

/* ==========================================================================
   الحدود
   ========================================================================== */

/** أعمقُ ما تُرسم به شجرةُ الوحدات — حمايةٌ من حلقةٍ لا تنتهي. */
export const MAX_UNIT_DEPTH = 8;

/* ==========================================================================
   طرق تسجيل الحضور
   ========================================================================== */

/**
 * بأيّ وسيلةٍ يُسجَّل الحضور.
 *
 * ## ولماذا فيها ما لم يُبنَ بعد
 *
 * السوقُ يعرف أربعًا: الموقع، وتطبيقُ الهاتف، وبصمةُ الإصبع، وبصمةُ الوجه.
 * وإخفاءُ ما لم يُبنَ يجعل من يُعدّ المؤسسةَ يظنّ النظامَ ناقصًا فيسأل،
 * وعرضُه مربّعًا يُعلَّم ثمّ لا يفعل شيئًا أسوأُ — يظنّه مشتغلًا فلا يكتشف
 * إلّا حين يشكو موظّفٌ أنّ بصمتَه لا تُسجَّل. فتُعرض كلُّها ويُقال عن كلٍّ
 * أمبنيّةٌ هي أم لا.
 */
export const CHECK_METHODS = ['WEB', 'MOBILE', 'FINGERPRINT', 'FACE'] as const;
export type CheckMethod = (typeof CHECK_METHODS)[number];

export interface CheckMethodDef extends Labelled<CheckMethod> {
  description: string;
  /** أمبنيّةٌ اليوم — أم اسمٌ محجوزٌ لما سيأتي. */
  built: boolean;
  /** ما ينقصها لتُبنى — يُعرض لمن يحاول اختيارَها. */
  missing?: string;
}

export const CHECK_METHOD_DEFS: readonly CheckMethodDef[] = [
  {
    key: 'WEB',
    label: 'من المتصفّح',
    kind: 'success',
    description: 'زرُّ الحضور في الشريط العلويّ، ومعه موقعُ الجهاز.',
    built: true,
  },
  {
    key: 'MOBILE',
    label: 'تطبيق الهاتف',
    kind: 'neutral',
    description: 'تسجيلٌ من تطبيقٍ مثبَّتٍ على هاتف الموظّف.',
    built: false,
    missing: 'لا تطبيقَ هاتفٍ بعد — والمتصفّحُ على الهاتف يؤدّي الغرضَ نفسَه.',
  },
  {
    key: 'FINGERPRINT',
    label: 'بصمة الإصبع',
    kind: 'neutral',
    description: 'جهازُ بصمةٍ عند الباب يُرسل السجلّات إلى النظام.',
    built: false,
    missing: 'يحتاج جهازًا ووصلةً إليه — ولكلّ صانعِ جهازٍ بروتوكولُه.',
  },
  {
    key: 'FACE',
    label: 'بصمة الوجه',
    kind: 'neutral',
    description: 'التعرّفُ على الوجه عند التسجيل.',
    built: false,
    missing: 'يحتاج جهازًا أو خدمةَ تعرّفٍ على الوجه — ولا واحدةَ منهما مربوطة.',
  },
] as const;

export const CHECK_METHOD_MAP = Object.fromEntries(
  CHECK_METHOD_DEFS.map((d) => [d.key, d]),
) as Readonly<Record<CheckMethod, CheckMethodDef>>;

/** ما يعمل اليوم فعلًا — تُبنى منه قيمةُ الخطّة الافتراضيّة. */
export const BUILT_CHECK_METHODS = CHECK_METHOD_DEFS.filter(
  (d) => d.built,
).map((d) => d.key);

/* ==========================================================================
   لائحة الجزاءات
   ========================================================================== */

/**
 * شريحةُ خصمٍ على التأخير أو الانصراف المبكّر.
 *
 * `toMinutes` فارغةً تعني شريحةً مفتوحةَ الطرف: «من تأخّر أكثرَ من ساعتين».
 * والخصمُ بأيّامِ الأجر لا بالمال: نسبةٌ من الراتب تُكتب رقمًا ثابتًا
 * تُصبح خطأً في أوّل علاوة.
 */
export interface DeductionRule {
  fromMinutes: number;
  toMinutes: number | null;
  deductionDays: number;
}

/**
 * خصمُ دقائقَ بعينها من لائحة.
 *
 * الشرائحُ تُقرأ «من `from` حتى `to`» — الطرفُ الأدنى داخلٌ والأعلى خارج،
 * فلا تقع دقيقةٌ في شريحتين. وأوّلُ شريحةٍ تنطبق هي التي تُحسب، فترتيبُ
 * اللائحة معنًى لا زينة.
 */
export function deductionFor(
  minutes: number,
  rules: readonly DeductionRule[],
): DeductionRule | null {
  if (minutes <= 0) return null;
  for (const rule of rules) {
    const under = rule.toMinutes === null || minutes < rule.toMinutes;
    if (minutes >= rule.fromMinutes && under) return rule;
  }
  return null;
}

/**
 * تداخلُ الشرائح.
 *
 * لائحةٌ فيها «من ١٥ إلى ٦٠» و«من ٣٠ إلى ٩٠» تُخصم من المتأخّر أربعين
 * دقيقةً بأوّلِ ما يُصادَف — فيختلف الخصمُ باختلافِ الترتيب وحدَه. تُمنع
 * عند الحفظ لا تُكتشف في كشف المرتّبات.
 */
export function overlappingRules(
  rules: readonly DeductionRule[],
): [number, number] | null {
  const sorted = rules
    .map((r, i) => ({ ...r, i }))
    .sort((a, b) => a.fromMinutes - b.fromMinutes);
  for (let i = 1; i < sorted.length; i++) {
    const prev = sorted[i - 1]!;
    const cur = sorted[i]!;
    if (prev.toMinutes === null || cur.fromMinutes < prev.toMinutes) {
      return [prev.i, cur.i];
    }
  }
  return null;
}

/** لائحةٌ شائعةٌ تُقترح عند إنشاء خطّةٍ جديدة — تُعدَّل وتُمسح. */
export const DEFAULT_LATE_RULES: readonly DeductionRule[] = [
  { fromMinutes: 16, toMinutes: 30, deductionDays: 0.25 },
  { fromMinutes: 30, toMinutes: 60, deductionDays: 0.5 },
  { fromMinutes: 60, toMinutes: null, deductionDays: 1 },
] as const;

/** أقصى عددِ شرائحَ في لائحةٍ واحدة — لائحةٌ أطولُ لا يقرؤها أحد. */
export const MAX_DEDUCTION_RULES = 8;

/**
 * قراءةُ لائحةِ جزاءاتٍ من عمودٍ حرّ.
 *
 * العمودُ JSON في قاعدة البيانات، فما يُقرأ منه يُرشَّح قبل أن يُحسب به:
 * صفٌّ كُتب قبل شكلٍ جديدٍ للّائحة لا يُسقط كشفَ مرتّباتٍ كاملًا، ويُهمَل
 * بندُه وحدَه.
 */
export function readDeductionRules(value: unknown): DeductionRule[] {
  if (!Array.isArray(value)) return [];
  return value.flatMap((row) => {
    if (!row || typeof row !== 'object' || Array.isArray(row)) return [];
    const r = row as Record<string, unknown>;
    const from = Number(r.fromMinutes);
    const amount = Number(r.deductionDays);
    if (!Number.isFinite(from) || !Number.isFinite(amount)) return [];
    const to =
      r.toMinutes === null || r.toMinutes === undefined
        ? null
        : Number(r.toMinutes);
    if (to !== null && !Number.isFinite(to)) return [];
    return [{ fromMinutes: from, toMinutes: to, deductionDays: amount }];
  });
}

/** طرقُ التسجيل المحفوظة — ويُسقط منها ما لم يعد في الكتالوج. */
export function readCheckMethods(value: unknown): CheckMethod[] {
  if (!Array.isArray(value)) return ['WEB'];
  const known = value.filter(
    (m): m is CheckMethod => typeof m === 'string' && m in CHECK_METHOD_MAP,
  );
  return known.length > 0 ? known : ['WEB'];
}

/** أيّامُ العمل المحفوظة — وفارغُها يعني «لا خطّةَ لهذا الموظّف». */
export function readWorkDays(value: unknown): number[] {
  if (!Array.isArray(value)) return [];
  return value.filter(
    (d): d is number => typeof d === 'number' && d >= 0 && d <= 6,
  );
}

/* ==========================================================================
   ما يقرؤه كشف المرتّبات من خطّة العمل
   ========================================================================== */

/**
 * خطّةُ عملٍ كما يحتاجها حسابُ الأجر.
 *
 * شكلٌ مستقلٌّ عن صفّ قاعدة البيانات: الحسابُ يحتاج أرقامًا لا صفًّا،
 * وفصلُهما يجعل قواعدَ الأجر تُختبر بلا قاعدة بيانات.
 */
export interface PayPlan {
  workDays: number[];
  /** ورديّةٌ تعبر منتصف الليل — يومُ عملها يقع في تاريخين. */
  overnight: boolean;
  absenceDeductionDays: number;
  forgotCheckoutDays: number;
  overtimeRate: number;
  holidayRate: number;
  lateRules: DeductionRule[];
  earlyRules: DeductionRule[];
}

/**
 * خطّةُ من لا خطّةَ له.
 *
 * ولا جزاءَ فيها: موظّفٌ لم تُسنَد إليه خطّةٌ بعدُ لا يُخصم منه بلائحةٍ
 * لم يقرّها أحد — يُحسب غيابُه يومًا بيومٍ كما كان، ويُترك تأخيرُه
 * مسجَّلًا بلا خصم.
 */
export const NO_PAY_PLAN: PayPlan = {
  workDays: [],
  overnight: false,
  absenceDeductionDays: 1,
  forgotCheckoutDays: 0,
  overtimeRate: 1,
  holidayRate: 1,
  lateRules: [],
  earlyRules: [],
};

/* ==========================================================================
   حسابُ اليوم على الوردية
   ========================================================================== */

/**
 * نافذةُ الدوام كما تُقاس.
 *
 * ## ولماذا الحسابُ هنا لا في خدمةٍ من الخدمتين
 *
 * التأخيرُ يُحسب في موضعين: زرُّ الحضور في الشريط، وورقةُ الحضور التي
 * يملؤها المسؤولُ بيده. وكانا حسابين مختلفين — الزرُّ يقرأ ورديّةَ صاحبه
 * والورقةُ تقرأ ساعةَ الشركة — فكان اليومُ الواحد يُقرأ تأخيرًا في شاشةٍ
 * وانضباطًا في أخرى، والذي يُصدَّق منهما هو الذي كُتب أخيرًا. فصار
 * الحسابُ واحدًا يقرؤه الاثنان.
 */
export interface AttendanceWindow {
  start: string;
  end: string;
  /** مهلةٌ يُتجاوز فيها عن التأخير — ويبقى تأخيرًا في السجلّ. */
  grace: number;
  /** إزاحةٌ مشروعةٌ لموعد البدء — تُزيح النهايةَ بمثلها. */
  flexible: number;
  overnight: boolean;
}

/** ساعاتُ الشركة العامّةُ نافذةً — لمن لا خطّةَ له. */
export function companyWindow(
  start: string,
  end: string,
  grace: number,
): AttendanceWindow {
  return { start, end, grace, flexible: 0, overnight: false };
}

/** دقائقُ اليومِ بتوقيت UTC — كما تُخزَّن الأوقاتُ كلُّها هنا. */
function clockMinutes(at: Date): number {
  return at.getUTCHours() * 60 + at.getUTCMinutes();
}

/**
 * بكم أزاح الموظّفُ يومَه ضمن المرونة المسموحة.
 *
 * من بدأ قبل الموعد لا يُزاح يومُه: الحضورُ المبكّر تطوّعٌ لا يُقصّر به
 * آخرُ الدوام. ومن بدأ بعد المرونة يُزاح بقدرها لا بقدر تأخّره — وإلّا
 * صار التأخيرُ يشتري لنفسه نهايةً مؤجَّلة.
 */
export function flexShiftOf(window: AttendanceWindow, checkIn: Date): number {
  if (window.flexible <= 0) return 0;
  const offset = clockMinutes(checkIn) - minutesOfTime(window.start);
  if (offset <= 0) return 0;
  return Math.min(offset, window.flexible);
}

/**
 * دقائقُ التأخير عن موعد البدء.
 *
 * المرونةُ تُطرح قبل المهلة: من يعمل بمرونة ساعةٍ ومهلةِ ربعٍ لا يُحسب
 * متأخّرًا حتى تمضي ساعةٌ وربع. وجمعُهما هو ما تعنيه الشركةُ حين تضبط
 * الاثنين معًا.
 */
export function lateMinutesIn(window: AttendanceWindow, checkIn: Date): number {
  const diff =
    clockMinutes(checkIn) -
    minutesOfTime(window.start) -
    window.grace -
    window.flexible;
  return diff > 0 ? diff : 0;
}

/**
 * نهايةُ الدوام المتوقَّعة لهذا اليوم بعينه.
 *
 * تُبنى من تاريخ الحضور لا من تاريخ اليوم: ورديّةٌ تبدأ العاشرةَ مساءً
 * وتنتهي السادسةَ صباحًا تنتهي في الغد، ومن يقيسها على اليوم نفسِه يحسب
 * لصاحبها ستَّ عشرةَ ساعةً سالبة — وهو ما كان يقع قبل هذا.
 */
export function expectedEnd(window: AttendanceWindow, checkIn: Date): Date {
  const dayStart = Date.UTC(
    checkIn.getUTCFullYear(),
    checkIn.getUTCMonth(),
    checkIn.getUTCDate(),
  );
  const end =
    minutesOfTime(window.end) +
    (window.overnight ? 24 * 60 : 0) +
    flexShiftOf(window, checkIn);
  return new Date(dayStart + end * 60_000);
}

/** دقائقُ الانصراف قبل موعده — صفرٌ لمن انصرف في موعده أو بعده. */
export function earlyMinutesIn(
  window: AttendanceWindow,
  checkIn: Date,
  checkOut: Date,
): number {
  const diff = expectedEnd(window, checkIn).getTime() - checkOut.getTime();
  return diff > 0 ? Math.round(diff / 60_000) : 0;
}

/** دقائقُ العمل بعد نهاية الدوام — صفرٌ لمن انصرف في موعده أو قبله. */
export function overtimeMinutesIn(
  window: AttendanceWindow,
  checkIn: Date,
  checkOut: Date,
): number {
  const diff = checkOut.getTime() - expectedEnd(window, checkIn).getTime();
  return diff > 0 ? Math.round(diff / 60_000) : 0;
}
